import {
  ProcessingRowField,
  ProcessingRowValues,
} from '../models/corporate-action-processing.model'

/**
 * Client-side validation for the Corporate Actions Processing grid (PVAL-25952).
 *
 * A pure module of plain functions over a row object so the rules are testable
 * without ag-grid, and so the one genuinely dangerous rule is pinned by a named
 * regression test.
 *
 * ## CUSIP: required and non-blank after trim — NO length or character-class rule
 *
 * The PVAL-25952 ticket text says "must be 9 alphanumeric characters". That is
 * SUPERSEDED by the finalized spec ("CUSIP validation — confirmed, non-blank is
 * correct") and by the shipped backend: PVAL-25926 commit 244ebc6772 removed the
 * length rule as a false-rejection bug, because Perseus enforces none and real
 * holdings use shorter pseudo-CUSIPs (e.g. the 7-character `PE32346` on beta).
 *
 * Client-side validation stricter than the shipped backend is a false-rejection
 * bug by construction, and in a grid the failure mode is worse than in the
 * backend: the row never becomes valid, Submit stays permanently disabled, and
 * there is no server round-trip to explain why. Do not re-add a length or
 * character-class check here — `corporate-actions-processing-validator.spec.ts`
 * asserts `PE32346` is valid.
 */

export interface ProcessingFieldError {
  field: ProcessingRowField
  message: string
}

/** `YYYY-MM-DD`, the only Ex Date form the backend's `LocalDate` accepts. */
export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * The DISPLAY Ex Date form, `MM/dd/yyyy`: what the column's placeholder shows
 * and what a US-locale Excel paste produces (PVAL-26303 #1).
 *
 * Month and day are 1-or-2 digits to match date-fns' numeric parsers, so
 * `8/6/2026` works here as it already does in Trade Entry Bulk Create. Groups
 * are `(month)(day)(year)`, read by `toIsoExDate`.
 */
export const DISPLAY_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/

export const MIN_NOTES_WORDS = 3

export const PROCESSING_ERROR_MESSAGES = {
  accountId: 'Account ID must be a positive integer no greater than 2147483647',
  exDate: 'Ex Date must be a valid YYYY-MM-DD or MM/DD/YYYY date',
  requiredCusip: 'CUSIP is required',
  requiredNumber: 'A finite number is required',
  nonNegative: 'Must be a finite number of zero or greater',
  strictlyPositive: 'Exchange rate must be a finite number greater than zero',
  notes: `Notes must contain at least ${MIN_NOTES_WORDS} words`,
}

/** Every field the validator inspects, in grid/column order. */
export const PROCESSING_VALIDATED_FIELDS: ProcessingRowField[] = [
  'accountId',
  'exDate',
  'targetCusip',
  'cashPerShare',
  'cashReceived',
  'destCusip',
  'destShareExchRate',
  'destCusipMarketPrice',
  'destCusip2',
  'destShareExchRate2',
  'destCusipMarketPrice2',
  'notes',
]

export function isBlank(value: string | undefined | null): boolean {
  return (value ?? '').trim().length === 0
}

/**
 * Trim, and tolerate an ISO date-time (`2026-08-06T00:00:00`) by keeping the
 * date part — some inline-edit date editors and Excel pastes produce one.
 * Anything else is handed to the pattern check unchanged so it fails loudly.
 */
export function normalizeExDate(value: string | undefined | null): string {
  const trimmed = (value ?? '').trim()
  const separatorIndex = trimmed.indexOf('T')
  return separatorIndex > 0 ? trimmed.slice(0, separatorIndex) : trimmed
}

/** True only for a real calendar day — rejects 2026-02-30 and 2026-13-01. */
function isRealCalendarDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) {
    return false
  }
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return day <= daysInMonth
}

/** True only for a real `YYYY-MM-DD` calendar date (rejects 2026-02-30). */
export function isValidIsoDate(value: string | undefined | null): boolean {
  const normalized = normalizeExDate(value)
  if (!ISO_DATE_PATTERN.test(normalized)) {
    return false
  }
  const [year, month, day] = normalized.split('-').map(Number)
  return isRealCalendarDate(year, month, day)
}

/**
 * True only for a real `MM/dd/yyyy` calendar date — the field's own placeholder
 * format (PVAL-26303 #1). Same calendar check as the ISO form, so `02/30/2026`
 * is rejected just as `2026-02-30` is.
 */
export function isValidDisplayDate(value: string | undefined | null): boolean {
  const match = DISPLAY_DATE_PATTERN.exec(normalizeExDate(value))
  if (!match) {
    return false
  }
  const [, month, day, year] = match
  return isRealCalendarDate(Number(year), Number(month), Number(day))
}

/**
 * Accepts either Ex Date form, matching `allowUpdateBasedOnType()`'s `'Date'`
 * case in `libs/create-transactions-modal`. Strict ISO alone flagged the field's
 * own placeholder format invalid, and `invalidRowCount > 0` disables Submit for
 * the whole grid, so one pasted `08/06/2026` blocked the batch (PVAL-26303 #1).
 *
 * Only safe because `toIsoExDate` converts the wire value. Do not widen this
 * without widening that: an unconverted display date fails Jackson for the WHOLE
 * request body.
 */
export function isValidExDate(value: string | undefined | null): boolean {
  return isValidIsoDate(value) || isValidDisplayDate(value)
}

/**
 * Convert an Ex Date cell to the ISO `yyyy-MM-dd` the backend's `LocalDate`
 * parses. A display-form value is rewritten and zero-padded; anything else is
 * passed through, so an invalid cell fails loudly rather than being reinterpreted.
 *
 * Separate from `normalizeExDate` on purpose: that feeds `isValidIsoDate`, which
 * must keep rejecting the display form.
 */
export function toIsoExDate(value: string | undefined | null): string {
  const normalized = normalizeExDate(value)
  const match = DISPLAY_DATE_PATTERN.exec(normalized)
  if (!match) {
    return normalized
  }
  const [, month, day, year] = match
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

/**
 * The largest `accountId` the backend can hold: `ProcessCashStockAcquisitionRow`
 * types it as a Kotlin `Int`, i.e. a signed 32-bit integer.
 *
 * This bound is the server's OWN range, so enforcing it here is not
 * client-stricter-than-server — it is the one place where NOT enforcing it
 * defeats the module's purpose. Without it, a pasted or mistyped
 * `12345678901234567890` passes validation, the row goes `valid`, Submit stays
 * enabled, and `toNumber` coerces it to `1.2345678901234568e19` for the POST.
 * Jackson then rejects the ENTIRE request body, so the response carries no
 * per-row `results` at all and every row in the batch is lost with no
 * actionable per-row message — the exact batch-level failure mode
 * `util/corporate-actions-processing-request.util.ts` exists to isolate.
 * Anything above 2^53 also loses precision silently before it even gets there.
 */
export const MAX_ACCOUNT_ID = 2147483647

/**
 * True for a positive integer with no sign, decimal point or exponent, within
 * the backend's `Int` range (1..`MAX_ACCOUNT_ID`) — see that constant for why
 * the upper bound is load-bearing rather than cosmetic.
 */
export function isPositiveInteger(value: string | undefined | null): boolean {
  const trimmed = (value ?? '').trim()
  if (!/^\d+$/.test(trimmed)) {
    return false
  }
  const parsed = Number(trimmed)
  return parsed > 0 && parsed <= MAX_ACCOUNT_ID
}

/**
 * Parse a cell value to a finite number, or `undefined` when it is blank or not
 * a number. `Number('')` is 0, which is why this cannot just call `Number`.
 */
export function parseFiniteNumber(
  value: string | undefined | null,
): number | undefined {
  const trimmed = (value ?? '').trim()
  if (trimmed.length === 0) {
    return undefined
  }
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : undefined
}

/** Count whitespace-separated words. */
export function countWords(value: string | undefined | null): number {
  const trimmed = (value ?? '').trim()
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length
}

/** True when every validated field is blank — a row the user has not started. */
export function isRowEmpty(values: ProcessingRowValues): boolean {
  return PROCESSING_VALIDATED_FIELDS.every(field => isBlank(values[field]))
}

function validateRequiredNumber(
  field: ProcessingRowField,
  value: string | undefined,
  strictlyPositive: boolean,
): ProcessingFieldError | undefined {
  const parsed = parseFiniteNumber(value)
  if (parsed === undefined) {
    return { field, message: PROCESSING_ERROR_MESSAGES.requiredNumber }
  }
  if (strictlyPositive && parsed <= 0) {
    return { field, message: PROCESSING_ERROR_MESSAGES.strictlyPositive }
  }
  if (!strictlyPositive && parsed < 0) {
    return { field, message: PROCESSING_ERROR_MESSAGES.nonNegative }
  }
  return undefined
}

function validateOptionalNumber(
  field: ProcessingRowField,
  value: string | undefined,
  strictlyPositive: boolean,
): ProcessingFieldError | undefined {
  if (isBlank(value)) {
    // Omitted — the backend applies its declared default
    // (destShareExchRate2 = 1.0, destCusipMarketPrice2 = 0.0).
    return undefined
  }
  return validateRequiredNumber(field, value, strictlyPositive)
}

/**
 * Required CUSIP: present and non-blank after trim. Deliberately no length or
 * character-class rule — see the module doc comment.
 */
export function validateRequiredCusip(
  field: ProcessingRowField,
  value: string | undefined,
): ProcessingFieldError | undefined {
  return isBlank(value)
    ? { field, message: PROCESSING_ERROR_MESSAGES.requiredCusip }
    : undefined
}

/**
 * Optional CUSIP: there is NO rule to break. Omitted, empty and whitespace-only
 * all mean "no second destination", so this never produces an error.
 *
 * It used to flag a whitespace-only value as `blankOptionalCusip`, and that was
 * a false rejection of exactly the kind this module's doc comment calls the
 * worst kind in a grid (PVAL-25952 code review). A Dest CUSIP 2 of `'   '`
 * pinned the row `invalid`, drove `invalidRowCount > 0` and disabled Submit for
 * the WHOLE grid — over a cell that looks empty on screen, reachable by typing a
 * space in the inline editor or by replacing a typed CUSIP with one.
 *
 * The rest of the code already read that value as absent, so the rejection was
 * also internally inconsistent and had NO wire effect to justify it:
 * `validateSecondDestinationNumbers` gates on `isBlank(values.destCusip2)` and
 * skips the numerics, and `toProcessingRequestRow` trims and omits `destCusip2`
 * from the body entirely. The client was rejecting a row the shipped backend
 * would have accepted, which the load-bearing CUSIP rule forbids: validation
 * must match the shipped backend and never be stricter.
 *
 * Kept as a named function rather than inlined: it is the explicit statement of
 * that rule at the call site in `validateProcessingRow`, and
 * `corporate-actions-processing-validator.spec.ts` pins it directly.
 */
export function validateOptionalCusip(
  field: ProcessingRowField,
  value: string | undefined,
): ProcessingFieldError | undefined {
  if (isBlank(value)) {
    // No second destination. Not an error — the same reading the numerics gate
    // and the request util already take.
    return undefined
  }
  // A value the user DID supply has no rule to break either: no length and no
  // character-class check, exactly as for a required CUSIP (module doc comment).
  return undefined
}

/**
 * Second-destination numeric rules, GATED on a second destination CUSIP being
 * supplied — mirroring the backend exactly.
 *
 * The Helios service builds its numerics map with
 * `if (row.destCusip2 != null) { put("destShareExchRate2", ...); put("destCusipMarketPrice2", ...) }`,
 * so with no second destination it does not validate those two fields at all.
 * Validating them whenever non-blank regardless would make the client STRICTER
 * than the shipped server — a false-rejection surface, and the worst kind in a
 * grid: a stale `0` left in Dest Share Exch Rate 2 from an abandoned second
 * destination would pin the row at `invalid` and disable Submit forever, with no
 * server round-trip to explain why.
 *
 * Because neither side checks those two fields without a Dest CUSIP 2, the
 * request util must not TRANSMIT the row's values for them either: with a blank
 * Dest CUSIP 2 it omits `destCusip2` and sends the backend's declared defaults
 * (1.0 / 0.0) instead of coercing the cells, so a stale value left behind by a
 * cleared second destination can never reach perseus unvalidated.
 */
function validateSecondDestinationNumbers(
  values: ProcessingRowValues,
): (ProcessingFieldError | undefined)[] {
  if (isBlank(values.destCusip2)) {
    return []
  }
  return [
    validateOptionalNumber(
      'destShareExchRate2',
      values.destShareExchRate2,
      true,
    ),
    validateOptionalNumber(
      'destCusipMarketPrice2',
      values.destCusipMarketPrice2,
      false,
    ),
  ]
}

/**
 * Validate one row. Returns every field-level error; an empty array means the
 * row is submittable.
 *
 * Rules (exactly the ticket's "Client-side validations", with the CUSIP rule
 * per the finalized spec):
 * - exchange rate(s): finite and strictly positive
 * - market prices / cash-per-share / cash-received (and the second-destination
 *   equivalents when supplied): finite and non-negative
 * - notes: 3 or more whitespace-separated words
 * - account ID: a positive integer inside the backend's `Int` range
 *   (1..`MAX_ACCOUNT_ID`)
 * - Ex Date: a valid `YYYY-MM-DD` or `MM/DD/YYYY` date, converted to ISO for
 *   the wire by `toIsoExDate` (PVAL-26303 #1)
 * - target + destination CUSIP: required, non-blank after trim
 * - Dest CUSIP 2: optional, with no rule at all — blank, empty and
 *   whitespace-only all read as "no second destination"
 * - second-destination numerics: only validated when Dest CUSIP 2 is supplied,
 *   mirroring the backend's own `if (row.destCusip2 != null)` gate
 */
export function validateProcessingRow(
  values: ProcessingRowValues,
): ProcessingFieldError[] {
  const errors: (ProcessingFieldError | undefined)[] = [
    isPositiveInteger(values.accountId)
      ? undefined
      : { field: 'accountId', message: PROCESSING_ERROR_MESSAGES.accountId },
    isValidExDate(values.exDate)
      ? undefined
      : { field: 'exDate', message: PROCESSING_ERROR_MESSAGES.exDate },
    validateRequiredCusip('targetCusip', values.targetCusip),
    validateRequiredNumber('cashPerShare', values.cashPerShare, false),
    validateRequiredNumber('cashReceived', values.cashReceived, false),
    validateRequiredCusip('destCusip', values.destCusip),
    validateRequiredNumber('destShareExchRate', values.destShareExchRate, true),
    validateRequiredNumber(
      'destCusipMarketPrice',
      values.destCusipMarketPrice,
      false,
    ),
    validateOptionalCusip('destCusip2', values.destCusip2),
    ...validateSecondDestinationNumbers(values),
    countWords(values.notes) >= MIN_NOTES_WORDS
      ? undefined
      : { field: 'notes', message: PROCESSING_ERROR_MESSAGES.notes },
  ]

  return errors.filter((error): error is ProcessingFieldError => !!error)
}

/** Group field errors by field, for the per-cell tooltip / cell colouring. */
export function toFieldErrorMap(
  errors: ProcessingFieldError[],
): Map<string, string[]> {
  const map = new Map<string, string[]>()
  errors.forEach(({ field, message }) => {
    const messages = map.get(field) ?? []
    messages.push(message)
    map.set(field, messages)
  })
  return map
}
